import {
  SectionElement,
  defineElement,
  on,
  query
} from "./chunk-DNDL4K7L.js";
import {
  __async,
  __decorateClass
} from "./chunk-XDKWOSVF.js";

// src/sections/shop-connector-search/shop-connector-search.ts
var ShopConnectorSearch = class extends SectionElement {
  constructor() {
    super(...arguments);
    this.debounceTimer = null;
    this.apiBaseUrl = "https://amphenol-fremdnummern-import.tim-maier.workers.dev";
    this.localStorageKey = "connector-search-history";
    this.maxHistoryItems = 3;
  }
  connectedCallback() {
    this.autocompletePopover.addEventListener("blur", (event) => this.handlePopoverBlur(event), true);
  }
  setState(state) {
    this.setAttribute("data-state", state);
    if (state !== "exact-match" && state !== "exact-match-identical") {
      this.setAttribute("data-has-vg-id", "false");
    }
  }
  handlePopoverBlur(event) {
    const relatedTarget = event.relatedTarget;
    if (relatedTarget && (this.autocompletePopover.contains(relatedTarget) || relatedTarget === this.searchInput)) {
      return;
    }
    this.autocompletePopover.classList.add("hidden");
  }
  onSearchButtonClick() {
    this.autocompletePopover.classList.add("hidden");
    const query2 = this.searchInput.value.trim();
    this.performSearch(query2);
  }
  onSearchInput() {
    const query2 = this.searchInput.value.trim();
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    if (query2.length === 0) {
      this.showSearchHistoryWithSuggestions();
      this.searchButton.setAttribute("disabled", "");
      this.setState("empty");
      return;
    }
    this.debounceTimer = window.setTimeout(() => {
      this.fetchAutocomplete(query2);
    }, 200);
    this.searchButton.removeAttribute("disabled");
  }
  onSearchFocus() {
    return __async(this, null, function* () {
      const query2 = this.searchInput.value.trim();
      if (query2.length === 0) {
        yield this.showSearchHistoryWithSuggestions();
      } else {
        this.fetchAutocomplete(query2);
      }
    });
  }
  onSearchKeydown(element, event) {
    if (event.key === "Enter") {
      event.preventDefault();
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
        this.debounceTimer = null;
      }
      this.autocompletePopover.classList.add("hidden");
      this.performSearch(element.value.trim());
    }
  }
  onSearchBlur(_, event) {
    const relatedTarget = event.relatedTarget;
    if (relatedTarget && (this.autocompletePopover.contains(relatedTarget) || this.searchButton.contains(relatedTarget))) {
      return;
    }
    this.autocompletePopover.classList.add("hidden");
  }
  fetchAutocomplete(query2) {
    return __async(this, null, function* () {
      try {
        const response = yield fetch(`${this.apiBaseUrl}/suggestV2?q=${encodeURIComponent(query2)}`);
        const data = yield response.json();
        if (data.matches.length > 0) {
          const autocompleteItems = data.matches.map((match) => ({ key: match, history: false }));
          this.renderAutocomplete(autocompleteItems);
          this.autocompletePopover.classList.remove("hidden");
        } else {
          this.autocompletePopover.classList.add("hidden");
        }
      } catch (error) {
        console.error("Autocomplete error:", error);
        this.autocompletePopover.classList.add("hidden");
      }
    });
  }
  performSearch(fremdnummer) {
    return __async(this, null, function* () {
      if (!fremdnummer)
        return;
      this.addToSearchHistory(fremdnummer);
      this.setState("loading");
      this.scrollIntoView({ behavior: "instant", block: "start" });
      try {
        const response = yield fetch(`${this.apiBaseUrl}/lookup/${encodeURIComponent(fremdnummer)}`);
        const data = yield response.json();
        if (data.exactMatch) {
          this.showExactMatch(data.exactMatch, fremdnummer);
        } else if (data.suggestions && data.suggestions.length > 0) {
          this.showSuggestions(data.suggestions, fremdnummer);
        } else {
          this.showNoMatch(fremdnummer);
        }
      } catch (error) {
        console.error("Search error:", error);
        this.setState("empty");
      }
      this.autocompletePopover.classList.add("hidden");
    });
  }
  renderAutocomplete(autocompleteItems) {
    this.autocompletePopover.innerHTML = autocompleteItems.map(
      (item) => `
        <button
          data-autocomplete-item="${this.escapeHtml(item.key)}"
          class="flex items-center gap-2 px-3 py-1.5 text-base text-left font-medium hover:bg-gray-100 rounded-[4px] w-full"
          tabindex="0"
        >
          ${item.history ? '<svg class="text-gray-700 w-5 h-5" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"><path fill="currentColor" d="M12 21q-3.15 0-5.575-1.912T3.275 14.2q-.1-.375.15-.687t.675-.363q.4-.05.725.15t.45.6q.6 2.25 2.475 3.675T12 19q2.925 0 4.963-2.037T19 12t-2.037-4.962T12 5q-1.725 0-3.225.8T6.25 8H8q.425 0 .713.288T9 9t-.288.713T8 10H4q-.425 0-.712-.288T3 9V5q0-.425.288-.712T4 4t.713.288T5 5v1.35q1.275-1.6 3.113-2.475T12 3q1.875 0 3.513.713t2.85 1.924t1.925 2.85T21 12t-.712 3.513t-1.925 2.85t-2.85 1.925T12 21m1-9.4l2.5 2.5q.275.275.275.7t-.275.7t-.7.275t-.7-.275l-2.8-2.8q-.15-.15-.225-.337T11 11.975V8q0-.425.288-.712T12 7t.713.288T13 8z"/></svg>' : ""}
          ${this.escapeHtml(item.key)}
        </button>
      `
    ).join("");
    this.autocompletePopover.querySelectorAll("[data-autocomplete-item]").forEach((item) => {
      item.addEventListener("click", () => this.handleAutocompleteClick(item));
    });
  }
  handleAutocompleteClick(element) {
    const value = element.dataset.autocompleteItem;
    if (value) {
      this.searchInput.value = value;
      this.autocompletePopover.classList.add("hidden");
      this.performSearch(value);
    }
    this.searchButton.removeAttribute("disabled");
  }
  showSuggestions(suggestions, searchQuery) {
    var _a;
    this.suggestionMatchText.textContent = ((_a = this.suggestionMatchText.dataset.text) != null ? _a : "").replace("%query%", searchQuery);
    this.suggestionMatchResult.innerHTML = suggestions.map(
      (suggestion) => `
        <button 
          data-suggestion-match-result-item="${this.escapeHtml(suggestion.currentKeyRaw)}" 
          class="w-full rounded-md px-4 py-2 border border-gray-200 hover:bg-gray-100 text-left"
        >
          <p class="text-black">${this.renderHighlightedDiffs(suggestion.highlightedDiffs)}</p>
          ${suggestion.internalId ? `<p class="text-xs text-gray-500">${this.escapeHtml(suggestion.internalId)}</p>` : ""}
        </button>
      `
    ).join("");
    this.suggestionMatchResult.querySelectorAll("[data-suggestion-match-result-item]").forEach((item) => {
      item.addEventListener("click", () => this.handleSuggestionClick(item));
    });
    this.setState("suggestions");
  }
  handleSuggestionClick(element) {
    const value = element.dataset.suggestionMatchResultItem;
    if (value) {
      this.searchInput.value = value;
      this.performSearch(value);
    }
    this.searchButton.removeAttribute("disabled");
  }
  showExactMatch(match, searchQuery) {
    const template = this.exactMatchText.dataset.text || this.exactMatchText.textContent || "";
    this.exactMatchText.textContent = template.replace("{{ query }}", searchQuery);
    if (this.searchedPartElement) {
      this.searchedPartElement.textContent = match.currentKeyRaw;
    }
    const hasVgId = match.vgId != null && match.vgId !== void 0;
    this.setAttribute("data-has-vg-id", hasVgId ? "true" : "false");
    if (this.vgPartElement && hasVgId) {
      this.vgPartElement.textContent = match.vgId;
    }
    if (this.compatiblePartElement) {
      const compatiblePart = match.internalId || match.fremdnummern[0] || "";
      this.compatiblePartElement.textContent = compatiblePart;
    }
    if (match.productHandle) {
      this.productButton.setAttribute("href", `/products/${match.productHandle}`);
      this.productButton.classList.remove("hidden");
    } else {
      this.productButton.classList.add("hidden");
    }
    const isIdentical = match.vgId && match.currentKeyRaw === match.vgId || match.internalId && match.currentKeyRaw === match.internalId;
    this.setState(isIdentical ? "exact-match-identical" : "exact-match");
  }
  showNoMatch(searchQuery) {
    const template = this.noMatchText.dataset.text || this.noMatchText.textContent || "";
    this.noMatchText.textContent = template.replace("{{ query }}", searchQuery);
    this.setState("no-match");
  }
  renderHighlightedDiffs(diffs) {
    return diffs.map((diff) => {
      switch (diff.type) {
        case "unchanged":
          return this.escapeHtml(diff.value);
        case "added":
          return `<span class="bg-green-300 font-bold">${this.escapeHtml(diff.value)}</span>`;
        case "removed":
          return `<span class="bg-red-300 font-bold line-through">${this.escapeHtml(diff.value)}</span>`;
        case "changed":
          return `<span class="bg-yellow-300 font-bold">${this.escapeHtml(diff.value)}</span>`;
        default:
          return "";
      }
    }).join("");
  }
  addToSearchHistory(query2) {
    const history = this.getSearchHistory();
    const filtered = history.filter((item) => item !== query2);
    filtered.unshift(query2);
    const newHistory = filtered.slice(0, this.maxHistoryItems);
    localStorage.setItem(this.localStorageKey, JSON.stringify(newHistory));
  }
  getSearchHistory() {
    try {
      const stored = localStorage.getItem(this.localStorageKey);
      return stored ? JSON.parse(stored) : [];
    } catch (e) {
      return [];
    }
  }
  showSearchHistoryWithSuggestions() {
    return __async(this, null, function* () {
      const history = this.getSearchHistory();
      const historyItems = history.map((key) => ({ key, history: true }));
      try {
        const response = yield fetch(`${this.apiBaseUrl}/suggestV2?q=`);
        const data = yield response.json();
        const suggestionItems = data.matches.map((key) => ({ key, history: false }));
        const autocompleteItems = [...historyItems, ...suggestionItems];
        if (autocompleteItems.length === 0) {
          this.autocompletePopover.classList.add("hidden");
          return;
        }
        this.renderAutocomplete(autocompleteItems);
        this.autocompletePopover.classList.remove("hidden");
      } catch (e) {
        if (historyItems.length === 0) {
          this.autocompletePopover.classList.add("hidden");
          return;
        }
        this.renderAutocomplete(historyItems);
        this.autocompletePopover.classList.remove("hidden");
      }
    });
  }
  escapeHtml(text) {
    const div = document.createElement("div");
    div.textContent = text;
    return div.innerHTML;
  }
};
__decorateClass([
  query("[data-connector-search-input]")
], ShopConnectorSearch.prototype, "searchInput", 2);
__decorateClass([
  query("[data-autocomplete-popover]")
], ShopConnectorSearch.prototype, "autocompletePopover", 2);
__decorateClass([
  query("[data-suggestion-match-result]")
], ShopConnectorSearch.prototype, "suggestionMatchResult", 2);
__decorateClass([
  query("[data-suggestion-match-text]")
], ShopConnectorSearch.prototype, "suggestionMatchText", 2);
__decorateClass([
  query("[data-no-match-text]")
], ShopConnectorSearch.prototype, "noMatchText", 2);
__decorateClass([
  query("[data-exact-match-text]")
], ShopConnectorSearch.prototype, "exactMatchText", 2);
__decorateClass([
  query("[data-product-button]")
], ShopConnectorSearch.prototype, "productButton", 2);
__decorateClass([
  query("[data-searched-part]")
], ShopConnectorSearch.prototype, "searchedPartElement", 2);
__decorateClass([
  query("[data-compatible-part]")
], ShopConnectorSearch.prototype, "compatiblePartElement", 2);
__decorateClass([
  query("[data-vg-part]")
], ShopConnectorSearch.prototype, "vgPartElement", 2);
__decorateClass([
  query("[data-connector-search-button]")
], ShopConnectorSearch.prototype, "searchButton", 2);
__decorateClass([
  on("click", "[data-connector-search-button]")
], ShopConnectorSearch.prototype, "onSearchButtonClick", 1);
__decorateClass([
  on("input", "[data-connector-search-input]")
], ShopConnectorSearch.prototype, "onSearchInput", 1);
__decorateClass([
  on("focus", "[data-connector-search-input]")
], ShopConnectorSearch.prototype, "onSearchFocus", 1);
__decorateClass([
  on("keydown", "[data-connector-search-input]")
], ShopConnectorSearch.prototype, "onSearchKeydown", 1);
__decorateClass([
  on("blur", "[data-connector-search-input]")
], ShopConnectorSearch.prototype, "onSearchBlur", 1);
ShopConnectorSearch = __decorateClass([
  defineElement("shop-connector-search")
], ShopConnectorSearch);
export {
  ShopConnectorSearch
};
